'use client';

import React from 'react';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import Image from 'next/image';
import Link from 'next/link';
import { Document, Node } from '@contentful/rich-text-types';

// Mark için tip tanımlaması
interface Mark {
  type: string;
}

// Embedded Asset node'u için tip tanımlaması
type EmbeddedAssetNode = Node & {
  data: {
    target?: {
      fields?: {
        title?: string;
        description?: string;
        file?: {
          url?: string;
          contentType?: string;
        };
      };
    };
  };
}

interface RichTextRendererProps {
  content: Document;
}

const RichTextRenderer: React.FC<RichTextRendererProps> = ({ content }) => {
  const options = {
    renderMark: {
      [MARKS.BOLD]: (text: React.ReactNode) => <strong className="font-bold">{text}</strong>,
      [MARKS.ITALIC]: (text: React.ReactNode) => <em className="italic">{text}</em>,
      [MARKS.UNDERLINE]: (text: React.ReactNode) => <u className="underline">{text}</u>,
      [MARKS.CODE]: (text: React.ReactNode) => (
        <code className="bg-gray-800 text-white px-1 py-0.5 rounded font-mono text-sm">{text}</code>
      ),
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => (
        <p className="mb-6 text-gray-300 leading-relaxed">{children}</p>
      ),
      [BLOCKS.HEADING_1]: (node: Node, children: React.ReactNode) => (
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-white">{children}</h1>
      ),
      [BLOCKS.HEADING_2]: (node: Node, children: React.ReactNode) => (
        <h2 className="text-2xl md:text-3xl font-bold mb-4 mt-8 text-white">{children}</h2>
      ),
      [BLOCKS.HEADING_3]: (node: Node, children: React.ReactNode) => (
        <h3 className="text-xl md:text-2xl font-bold mb-4 mt-6 text-white">{children}</h3>
      ),
      [BLOCKS.HEADING_4]: (node: Node, children: React.ReactNode) => (
        <h4 className="text-lg md:text-xl font-bold mb-3 mt-6 text-white">{children}</h4>
      ),
      [BLOCKS.HEADING_5]: (node: Node, children: React.ReactNode) => (
        <h5 className="text-base md:text-lg font-bold mb-3 mt-4 text-white">{children}</h5>
      ),
      [BLOCKS.HEADING_6]: (node: Node, children: React.ReactNode) => (
        <h6 className="text-sm md:text-base font-bold mb-2 mt-4 text-white">{children}</h6>
      ),
      [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) => (
        <ul className="list-disc pl-6 mb-6 text-gray-300 space-y-2">{children}</ul>
      ),
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => (
        <ol className="list-decimal pl-6 mb-6 text-gray-300 space-y-2">{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node: Node, children: React.ReactNode) => (
        <li className="leading-relaxed">{children}</li>
      ),
      [BLOCKS.QUOTE]: (node: Node, children: React.ReactNode) => (
        <blockquote className="border-l-4 border-blue-500 pl-4 py-1 mb-6 italic text-gray-400">
          {children}
        </blockquote>
      ),
      [BLOCKS.HR]: () => <hr className="border-gray-700 my-8" />,
      [BLOCKS.EMBEDDED_ASSET]: (node: Node) => {
        // Tip güvenliği için tanımlı arayüzü kullanarak tip iddiası yapıyoruz
        const nodeData = (node as EmbeddedAssetNode).data?.target?.fields;
        if (!nodeData) return null;
        
        const { title, description, file } = nodeData;
        if (!file) return null;
        
        const { url, contentType } = file;
        if (!url || !contentType) return null;

        if (contentType.includes('image')) {
          return (
            <div className="my-8">
              <div className="relative w-full h-auto min-h-[300px] md:min-h-[400px] rounded-lg overflow-hidden">
                <Image 
                  src={`https:${url}`}
                  alt={description || title || 'Blog image'}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 800px"
                />
              </div>
              {description && (
                <p className="mt-2 text-sm text-gray-500 text-center">{description}</p>
              )}
            </div>
          );
        }

        // Diğer dosya türleri için (video, PDF vb.)
        return (
          <div className="my-4">
            <a 
              href={`https:${url}`} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {title || 'Download file'}
            </a>
          </div>
        );
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
        const url = (node as { data?: { uri?: string } }).data?.uri;
        if (!url) return children;
        
        const isInternal = url.startsWith('/') || url.includes('fynwise.com');

        if (isInternal) {
          return (
            <Link 
              href={url} 
              className="text-blue-400 hover:text-blue-300 underline"
            >
              {children}
            </Link>
          );
        }

        return (
          <a 
            href={url} 
            target="_blank" 
            rel="noopener noreferrer" 
            className="text-blue-400 hover:text-blue-300 underline"
          >
            {children}
          </a>
        );
      },
      [BLOCKS.TABLE]: (node: Node, children: React.ReactNode) => (
        <div className="overflow-x-auto mb-6">
          <table className="min-w-full border-collapse border border-gray-700">
            {children}
          </table>
        </div>
      ),
      [BLOCKS.TABLE_ROW]: (node: Node, children: React.ReactNode) => (
        <tr className="border-b border-gray-700">{children}</tr>
      ),
      [BLOCKS.TABLE_CELL]: (node: Node, children: React.ReactNode) => {
        // Daha spesifik tiplerle erişim
        const nodeContent = (node as { content?: Array<{ 
          nodeType?: string; 
          content?: Array<{ 
            marks?: Array<Mark> 
          }> 
        }> }).content;
        
        // Null check ve güvenli erişim
        const isHeader = nodeContent?.[0]?.nodeType === 'paragraph' && 
                         nodeContent[0]?.content?.[0]?.marks?.
                         some((mark: Mark) => mark.type === 'bold');
        
        return isHeader ? (
          <th className="px-4 py-2 text-left bg-gray-800 font-semibold">{children}</th>
        ) : (
          <td className="px-4 py-2 border-r border-gray-700 last:border-r-0">{children}</td>
        );
      },
      [BLOCKS.EMBEDDED_ENTRY]: () => {
        // Kullanılmayan parametreleri kaldırdık
        return <div className="my-4 p-4 bg-gray-800 rounded-lg">Embedded Content</div>;
      },
    },
  };

  return (
    <div className="prose prose-invert prose-lg max-w-none">
      {documentToReactComponents(content, options)}
    </div>
  );
};

export default RichTextRenderer;